/**
 * MyMonthStrip — "My timesheet · {month}" dashboard widget for monthly cadence.
 *
 * NEW FILE. Drop into:
 *   web/src/components/dashboard-widgets/components/MyMonthStrip.tsx
 *
 * Sibling of MyWeekStrip: same PanelCard shell, same theme tokens
 * (statusSoft.*, accent.tint), same xs/sm fallback pattern. Seven day cells
 * become five week roll-ups; a month progress bar leads; the footer trades
 * Variance for Remaining + a Submit-by line.
 */

import { Box, Link, Stack, Typography } from '@mui/material';
import { displaySx, numericSx } from '../../../theme/typography';
import { PanelCard } from './PanelCard';
import type { MyWeekWidget, MyMonthWeek } from '../types';
import type { NavigateToTimesheetsOptions } from '../navigation';

interface MyMonthStripProps {
  data: MyWeekWidget | undefined;
  loading?: boolean;
  onNavigateToTimesheets?: (options?: NavigateToTimesheetsOptions) => void;
}

export function MyMonthStrip({ data, loading, onNavigateToTimesheets }: MyMonthStripProps) {
  const weeks = data?.weeks ?? [];
  const title = `My timesheet · ${data?.monthLabel ?? 'this month'}`;
  const logged = data?.hoursEntered ?? 0;
  const expected = data?.expectedHours ?? 0;
  const remaining = data?.remainingHours ?? 0;
  const pct = data?.completionPct ?? 0;
  const statusLabel = formatStatus(data?.status);
  const statusColour = data?.status === 'DRAFT' ? 'warning.main' : 'text.primary';

  const headerAction = onNavigateToTimesheets ? (
    <Link
      component="button"
      type="button"
      onClick={() => onNavigateToTimesheets({ tab: 'my-timesheet', monthISO: data?.monthISO })}
      underline="hover"
      sx={{ fontSize: '0.8125rem' }}
    >
      Open timesheet →
    </Link>
  ) : undefined;

  if (loading && !data) {
    return (
      <PanelCard title={title} action={headerAction} pad={18} data-testid="widget-my-month-strip">
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>Loading…</Typography>
      </PanelCard>
    );
  }
  if (!data || weeks.length === 0) {
    return (
      <PanelCard title={title} action={headerAction} pad={18} data-testid="widget-my-month-strip">
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
          No timesheet period yet for this month.
        </Typography>
      </PanelCard>
    );
  }

  return (
    <PanelCard title={title} action={headerAction} pad={0} data-testid="widget-my-month-strip">
      {/* Month progress bar */}
      <Box sx={{ px: '18px', pt: '14px', pb: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
          <Box component="span" sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
            <Box component="b" sx={[numericSx, { fontSize: '0.9375rem', fontWeight: 700 }]}>{formatHours(logged)}</Box>
            <Box component="span" sx={[numericSx, { color: 'text.secondary' }]}> / {formatHours(expected)}h</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}> · {pct}%</Box>
          </Box>
          {data.submitByLabel ? (
            <Box component="span" sx={{ fontSize: '0.71875rem', color: 'text.secondary' }}>
              Submit by <Box component="b" sx={{ color: 'text.primary' }}>{data.submitByLabel}</Box>
              {remaining > 0 ? ` · ${formatHours(remaining)}h to log` : ''}
            </Box>
          ) : null}
        </Box>
        <Box sx={(t) => ({ height: 8, borderRadius: '4px', backgroundColor: t.palette.action.hover, overflow: 'hidden' })}>
          <Box sx={(t) => ({ width: `${pct}%`, height: '100%', borderRadius: '4px', backgroundColor: t.palette.primary.main })} />
        </Box>
      </Box>

      {/* Desktop 5-week strip — hidden on xs/sm */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box
          sx={(t) => ({
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(weeks.length, 6)}, 1fr)`,
            borderTop: `1px solid ${t.palette.divider}`,
            borderBottom: `1px solid ${t.palette.divider}`,
            mt: '14px',
          })}
        >
          {weeks.map((w, i) => (
            <WeekCell key={w.label} week={w} isLast={i === weeks.length - 1} />
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', px: '18px', py: '12px', gap: 1 }}>
          <FooterStat label="Logged" value={`${formatHours(logged)}h`} />
          <FooterStat label="Expected" value={`${formatHours(expected)}h`} />
          <FooterStat label="Remaining" value={`${formatHours(remaining)}h`} valueColor={remaining > 0 ? 'warning.main' : 'success.main'} />
          <FooterStat label="Status" value={statusLabel} valueColor={statusColour} align="right" />
        </Box>
      </Box>

      {/* Mobile fallback */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, px: '18px', py: '16px' }}>
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
          <FooterStat label="Logged" value={`${formatHours(logged)}h`} />
          <FooterStat label="Expected" value={`${formatHours(expected)}h`} />
          <FooterStat label="Remaining" value={`${formatHours(remaining)}h`} valueColor={remaining > 0 ? 'warning.main' : 'success.main'} />
        </Stack>
      </Box>
    </PanelCard>
  );
}

// ─── week cell ───────────────────────────────────────────────────────────
function WeekCell({ week, isLast }: { week: MyMonthWeek; isLast: boolean }) {
  // The bar is full at 100%; the server's figure is not clamped.
  const pct = Math.min(100, week.completionPct);
  const { state } = week;

  return (
    <Box
      sx={(t) => {
        const bgByState = { ok: t.palette.statusSoft.ok, under: t.palette.statusSoft.warn, future: 'transparent' };
        return {
          position: 'relative',
          textAlign: 'center',
          py: '12px',
          px: '10px',
          backgroundColor: week.isCurrent ? t.palette.accent.tint : bgByState[state],
          borderRight: isLast ? 'none' : `1px solid ${t.palette.divider}`,
        };
      }}
    >
      {week.isCurrent ? (
        <Box component="span" title="Current week" sx={{ position: 'absolute', top: 6, right: 8, fontSize: '0.5625rem', color: 'primary.main', fontWeight: 700, letterSpacing: '0.04em' }}>
          THIS WK
        </Box>
      ) : null}
      <Typography component="div" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1 }}>
        {week.label}
      </Typography>
      <Typography component="div" sx={[numericSx, { fontSize: '0.625rem', color: 'text.disabled', mt: '1px' }]}>
        {week.rangeLabel}
      </Typography>
      <Typography
        component="div"
        sx={[numericSx, displaySx, {
          fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.05, mt: '7px',
          color: state === 'future' ? 'text.disabled' : state === 'under' ? 'warning.main' : 'success.main',
        }]}
      >
        {formatHours(week.loggedHours)}
        <Box component="span" sx={{ fontSize: '0.625rem', color: 'text.disabled', ml: '1px' }}>/{formatHours(week.targetHours)}h</Box>
      </Typography>
      <Box sx={(t) => ({ height: 4, borderRadius: '2px', mx: '8px', mt: '7px', backgroundColor: t.palette.action.hover, overflow: 'hidden' })}>
        <Box
          sx={(t) => ({
            width: `${pct}%`, height: '100%', borderRadius: '2px',
            backgroundColor: state === 'future' ? t.palette.divider : state === 'under' ? t.palette.warning.main : t.palette.success.main,
          })}
        />
      </Box>
      {week.isSplit ? (
        <Box
          component="span"
          title="Split across two months — only the in-month days count here"
          sx={(t) => ({
            display: 'inline-block', mt: '4px', fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '0.04em',
            textTransform: 'uppercase', color: 'primary.main', backgroundColor: t.palette.accent.tint,
            border: `1px solid ${t.palette.accent.soft ?? t.palette.divider}`, borderRadius: '4px', px: '4px',
          })}
        >
          partial wk
        </Box>
      ) : null}
    </Box>
  );
}

// ─── helpers (shared shape with MyWeekStrip) ───────────────────────────────
interface FooterStatProps { label: string; value: string; valueColor?: string; align?: 'left' | 'right'; }
function FooterStat({ label, value, valueColor = 'text.primary', align = 'left' }: FooterStatProps) {
  return (
    <Box sx={{ textAlign: align }}>
      <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.75rem', mr: '6px' }}>{label}</Box>
      <Box component="span" sx={[numericSx, { color: valueColor, fontWeight: 600, fontSize: '0.8125rem' }]}>{value}</Box>
    </Box>
  );
}
function formatHours(n: number): string { return Number.isInteger(n) ? String(n) : n.toFixed(1); }
function formatStatus(status: MyWeekWidget['status'] | undefined): string {
  switch (status) {
    case 'NOT_STARTED': return 'Not started';
    case 'DRAFT': return 'Draft';
    case 'SUBMITTED': return 'Submitted';
    case 'APPROVED': return 'Approved';
    case 'REJECTED': return 'Rejected';
    default: return '—';
  }
}
